<template>
  <div class="emp-attendance-stat-v3" v-loading="loadingSummary || loadingTrend">
    <!-- Header Section -->
    <div class="stat-section-header">
      <div class="header-title-group">
        <div class="header-icon-badge">
          <i class="el-icon-s-cooperation"></i>
        </div>
        <div>
          <h2 class="header-title">Thống Kê Chuyên Cần & Chấm Công Nhân Sự</h2>
          <p class="header-subtitle">Theo dõi tình hình điểm danh đi làm, nghỉ phép, đi muộn và về sớm của cán bộ - giáo viên - nhân viên</p>
        </div>
      </div>

      <div class="header-badge-group">
        <span v-if="hasSummary" class="live-indicator">
          <span class="pulse-dot"></span> Đi làm: {{ employeeStats.present }}/{{ employeeStats.totalEmployees }} nhân sự ({{ formatRate(employeeStats.attendanceRate) }}%)
        </span>
      </div>
    </div>

    <el-alert v-if="apiError" :title="apiError" type="error" :closable="false" show-icon class="stat-api-error" />

    <!-- 1. Cụm 6 chỉ số nhân sự: Tổng số nhân sự, Đi làm, Nghỉ có phép, Nghỉ không phép, Đi muộn, Về sớm -->
    <div class="emp-kpi-grid">
      <!-- 1. Tổng số nhân sự -->
      <div class="emp-kpi-card border-indigo">
        <div class="kpi-icon-wrap bg-indigo-soft text-indigo">
          <i class="el-icon-user"></i>
        </div>
        <div class="kpi-details">
          <span class="kpi-name">Tổng số nhân sự</span>
          <div class="kpi-number text-indigo">{{ displayCount(employeeStats.totalEmployees) }} <span class="kpi-unit">người</span></div>
          <div class="kpi-hint">Toàn bộ trường</div>
        </div>
      </div>

      <!-- 2. Đi làm -->
      <div class="emp-kpi-card border-emerald">
        <div class="kpi-icon-wrap bg-emerald-soft text-emerald">
          <i class="el-icon-circle-check"></i>
        </div>
        <div class="kpi-details">
          <span class="kpi-name">Đi làm</span>
          <div class="kpi-number text-emerald">{{ displayCount(employeeStats.present) }} <span class="kpi-unit">người</span></div>
          <div class="kpi-hint text-emerald"><i class="el-icon-check"></i> {{ hasSummary ? `${formatRate(employeeStats.attendanceRate)}% nhân sự` : (loadingSummary ? "Đang tải số liệu" : "Chưa có số liệu") }}</div>
        </div>
      </div>

      <!-- 3. Nghỉ có phép -->
      <div class="emp-kpi-card border-yellow">
        <div class="kpi-icon-wrap bg-yellow-soft text-yellow">
          <i class="el-icon-warning-outline"></i>
        </div>
        <div class="kpi-details">
          <span class="kpi-name">Nghỉ có phép</span>
          <div class="kpi-number text-yellow">{{ displayCount(employeeStats.absentPermitted) }} <span class="kpi-unit">người</span></div>
          <div class="kpi-hint">Đã ghi nhận có phép</div>
        </div>
      </div>

      <!-- 4. Nghỉ không phép -->
      <div class="emp-kpi-card border-rose">
        <div class="kpi-icon-wrap bg-rose-soft text-rose">
          <i class="el-icon-circle-close"></i>
        </div>
        <div class="kpi-details">
          <span class="kpi-name">Nghỉ không phép</span>
          <div class="kpi-number text-rose">{{ displayCount(employeeStats.absentUnpermitted) }} <span class="kpi-unit">người</span></div>
          <div class="kpi-hint">Chưa rõ lý do</div>
        </div>
      </div>

      <!-- 5. Đi muộn -->
      <div class="emp-kpi-card border-amber">
        <div class="kpi-icon-wrap bg-amber-soft text-amber">
          <i class="el-icon-time"></i>
        </div>
        <div class="kpi-details">
          <span class="kpi-name">Đi muộn</span>
          <div class="kpi-number text-amber">{{ displayCount(employeeStats.lateArrivals) }} <span class="kpi-unit">người</span></div>
          <div class="kpi-hint">Theo giờ quy định của trường</div>
        </div>
      </div>

      <!-- 6. Về sớm -->
      <div class="emp-kpi-card border-purple">
        <div class="kpi-icon-wrap bg-purple-soft text-purple">
          <i class="el-icon-right"></i>
        </div>
        <div class="kpi-details">
          <span class="kpi-name">Về sớm</span>
          <div class="kpi-number text-purple">{{ displayCount(employeeStats.earlyDepartures) }} <span class="kpi-unit">người</span></div>
          <div class="kpi-hint">Trước giờ quy định</div>
        </div>
      </div>
    </div>

    <!-- 2. Khu vực Biểu đồ trực quan: Tỷ lệ đi làm & Xu hướng 7 ngày qua -->
    <el-row :gutter="20" class="charts-row">
      <!-- Biểu đồ 1: Tỷ lệ đi làm hôm nay -->
      <el-col :xs="24" :sm="24" :md="9" :lg="8">
        <div class="chart-card h-100">
          <div class="chart-card-head">
            <div>
              <h3 class="chart-title">Tỷ Lệ Đi Làm Hôm Nay</h3>
              <p class="chart-subtitle">So với tổng số {{ displayCount(employeeStats.totalEmployees) }} nhân sự đang làm</p>
            </div>
            <span class="tag-pill tag-pill--emerald">Hôm nay</span>
          </div>

          <div v-if="hasSummary" class="chart-container" ref="chartEmpGauge"></div>
          <div v-else class="chart-container chart-empty">{{ loadingSummary ? "Đang tải số liệu" : "Chưa có số liệu" }}</div>

          <div class="gauge-footer-grid">
            <div class="mini-stat-card">
              <span class="ms-label">Đúng giờ</span>
              <strong class="ms-val text-emerald">{{ displayCount(employeeStats.onTime) }} người</strong>
            </div>
            <div class="mini-stat-card">
              <span class="ms-label">Đi muộn</span>
              <strong class="ms-val text-amber">{{ displayCount(employeeStats.lateArrivals) }} người</strong>
            </div>
            <div class="mini-stat-card">
              <span class="ms-label">Vắng mặt</span>
              <strong class="ms-val text-rose">{{ displayCount(employeeStats.absentTotal) }} người</strong>
            </div>
          </div>
          <p v-if="hasSummary && employeeStats.unmarked > 0" class="unmarked-note">Chưa chấm công: {{ employeeStats.unmarked }} người</p>
        </div>
      </el-col>

      <!-- Biểu đồ 2: Xu hướng chuyên cần 7 ngày qua (Thứ 2 -> Chủ Nhật) -->
      <el-col :xs="24" :sm="24" :md="15" :lg="16">
        <div class="chart-card h-100">
          <div class="chart-card-head">
            <div>
              <h3 class="chart-title">Xu Hướng Chuyên Cần 7 Ngày Qua</h3>
              <p class="chart-subtitle">Số người đi làm và tỷ lệ theo từng ngày trong 7 ngày gần nhất</p>
            </div>
            <span class="tag-pill tag-pill--blue">7 ngày gần nhất</span>
          </div>

          <div v-if="hasTrend" class="chart-container" ref="chartEmpTrend"></div>
          <div v-else class="chart-container chart-empty">{{ loadingTrend ? "Đang tải xu hướng" : "Chưa có số liệu" }}</div>

          <div class="trend-legend-row">
            <div class="legend-chip">
              <span class="chip-color chip-bar"></span>
              <span>Đi làm (người)</span>
            </div>
            <div class="legend-chip">
              <span class="chip-color chip-bg"></span>
              <span>Tổng nhân sự (người)</span>
            </div>
            <div class="legend-chip">
              <span class="chip-color chip-line"></span>
              <span>Tỷ lệ đi làm (%)</span>
            </div>
            <div class="legend-chip">
              <span class="chip-color chip-late"></span>
              <span>Đi muộn (người)</span>
            </div>
          </div>
        </div>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import * as echarts from "echarts";
import EmployeeAttendanceStatisticsService from "@/services/dashboard_v3/EmployeeAttendanceStatisticsService";

const emptySummary = () => ({
  date: null,
  totalEmployees: null,
  present: null,
  absentPermitted: null,
  absentUnpermitted: null,
  unmarked: null,
  lateArrivals: null,
  earlyDepartures: null,
  onTime: null,
  absentTotal: null,
  attendanceRate: null
});

export default {
  name: "EmployeeAttendanceStatisticsV3",
  props: {
    autoLoad: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      hasLoadedData: false,
      isLoadingData: false,
      employeeStats: emptySummary(),
      trend: null,
      loadingSummary: false,
      loadingTrend: false,
      summaryError: "",
      trendError: "",
      summaryRequestId: 0,
      trendRequestId: 0,
      chartGauge: null,
      chartTrend: null
    };
  },

  computed: {
    hasSummary() {
      return this.employeeStats.date !== null;
    },
    hasTrend() {
      return this.trend !== null && Array.isArray(this.trend.points) && this.trend.points.length > 0;
    },
    apiError() {
      return [this.summaryError, this.trendError].filter(Boolean).join(" ");
    }
  },

  mounted() {
    this.$nextTick(() => {
      window.addEventListener("resize", this.handleResize);
      if (this.autoLoad) {
        this.loadData();
      }
    });
  },

  beforeDestroy() {
    this.summaryRequestId++;
    this.trendRequestId++;
    window.removeEventListener("resize", this.handleResize);
    this.disposeCharts();
  },

  methods: {
    async loadData() {
      if (this.hasLoadedData || this.isLoadingData) return;
      this.isLoadingData = true;
      try {
        await this.loadSummary();
        await this.loadTrend();
        this.hasLoadedData = true;
      } finally {
        this.isLoadingData = false;
      }
    },

    displayCount(value) {
      return value === null || value === undefined ? "—" : value;
    },

    formatRate(value) {
      return Number(value).toFixed(1);
    },

    async loadSummary() {
      const requestId = ++this.summaryRequestId;
      this.loadingSummary = true;
      this.summaryError = "";
      try {
        const response = await EmployeeAttendanceStatisticsService.getSummary();
        if (requestId !== this.summaryRequestId) return;
        const data = response.data && response.data.data;
        if (!data || !data.date || data.totalEmployees === undefined || data.attendanceRate === undefined) {
          throw new Error("Invalid employee attendance summary response");
        }
        this.employeeStats = data;
        this.$nextTick(() => this.renderGaugeChart());
      } catch (error) {
        if (requestId !== this.summaryRequestId) return;
        if (this.chartGauge) {
          this.chartGauge.dispose();
          this.chartGauge = null;
        }
        this.employeeStats = emptySummary();
        this.summaryError = "Không tải được thống kê chấm công nhân sự hôm nay.";
      } finally {
        if (requestId === this.summaryRequestId) this.loadingSummary = false;
      }
    },

    async loadTrend() {
      const requestId = ++this.trendRequestId;
      this.loadingTrend = true;
      this.trendError = "";
      try {
        const response = await EmployeeAttendanceStatisticsService.getTrend();
        if (requestId !== this.trendRequestId) return;
        const data = response.data && response.data.data;
        if (!data || !Array.isArray(data.points) || data.points.length !== 7) {
          throw new Error("Invalid employee attendance trend response");
        }
        this.trend = data;
        this.$nextTick(() => this.renderTrendChart());
      } catch (error) {
        if (requestId !== this.trendRequestId) return;
        if (this.chartTrend) {
          this.chartTrend.dispose();
          this.chartTrend = null;
        }
        this.trend = null;
        this.trendError = "Không tải được xu hướng chấm công nhân sự.";
      } finally {
        if (requestId === this.trendRequestId) this.loadingTrend = false;
      }
    },

    handleResize() {
      if (this.chartGauge) this.chartGauge.resize();
      if (this.chartTrend) this.chartTrend.resize();
    },

    disposeCharts() {
      if (this.chartGauge) {
        this.chartGauge.dispose();
        this.chartGauge = null;
      }
      if (this.chartTrend) {
        this.chartTrend.dispose();
        this.chartTrend = null;
      }
    },

    // 1. Biểu đồ Gauge tỷ lệ đi làm hôm nay
    renderGaugeChart() {
      const dom = this.$refs.chartEmpGauge;
      if (!dom) return;
      if (!this.chartGauge) {
        this.chartGauge = echarts.init(dom);
      }

      const rate = Number(this.employeeStats.attendanceRate);

      this.chartGauge.setOption({
        series: [
          {
            type: "gauge",
            startAngle: 210,
            endAngle: -30,
            min: 0,
            max: 100,
            splitNumber: 10,
            itemStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
                { offset: 0, color: "#6366f1" },
                { offset: 0.5, color: "#3b82f6" },
                { offset: 1, color: "#10b981" }
              ]),
              shadowColor: "rgba(16, 185, 129, 0.35)",
              shadowBlur: 10
            },
            progress: {
              show: true,
              roundCap: true,
              width: 14
            },
            pointer: {
              show: false
            },
            axisLine: {
              roundCap: true,
              lineStyle: {
                width: 14,
                color: [[1, "#f1f5f9"]]
              }
            },
            axisTick: { show: false },
            splitLine: { show: false },
            axisLabel: { show: false },
            title: {
              show: true,
              offsetCenter: [0, "20%"],
              fontSize: 13,
              color: "#64748b",
              fontWeight: 600
            },
            detail: {
              valueAnimation: true,
              offsetCenter: [0, "-15%"],
              fontSize: 34,
              fontWeight: 800,
              formatter: "{value}%",
              color: "#0f172a"
            },
            data: [
              {
                value: rate,
                name: "Tỷ lệ đi làm"
              }
            ]
          }
        ]
      });
    },

    // 2. Biểu đồ Xu hướng chuyên cần 7 ngày gần nhất
    renderTrendChart() {
      const dom = this.$refs.chartEmpTrend;
      if (!dom) return;
      if (!this.chartTrend) {
        this.chartTrend = echarts.init(dom);
      }

      const points = this.trend.points;
      this.chartTrend.setOption({
        tooltip: {
          trigger: "axis",
          backgroundColor: "rgba(15, 23, 42, 0.9)",
          borderColor: "transparent",
          padding: [10, 14],
          textStyle: { color: "#ffffff", fontSize: 13 },
          formatter: (params) => {
            const idx = params[0].dataIndex;
            const point = points[idx];

            return `
              <div style="font-weight:700;margin-bottom:6px;color:#f8fafc;border-bottom:1px solid rgba(255,255,255,0.15);padding-bottom:4px;">
                ${point.label}, ${point.date}
              </div>
              <div style="display:flex;justify-content:space-between;gap:16px;margin:3px 0;">
                <span style="color:#818cf8;">● Đi làm:</span>
                <b>${point.present}/${point.totalEmployees} người</b>
              </div>
              <div style="display:flex;justify-content:space-between;gap:16px;margin:3px 0;color:#34d399;">
                <span>● Tỷ lệ chuyên cần:</span>
                <b>${this.formatRate(point.attendanceRate)}%</b>
              </div>
              <div style="display:flex;justify-content:space-between;gap:16px;margin:3px 0;color:#fbbf24;">
                <span>● Đi muộn:</span>
                <b>${point.lateArrivals} người</b>
              </div>
            `;
          }
        },
        grid: {
          top: 35,
          left: 10,
          right: 15,
          bottom: 10,
          containLabel: true
        },
        xAxis: {
          type: "category",
          data: points.map(point => point.label),
          axisLine: { lineStyle: { color: "#cbd5e1" } },
          axisTick: { show: false },
          axisLabel: { color: "#475569", fontWeight: 600, fontSize: 12 }
        },
        yAxis: [
          // Trục trái: Số lượng nhân sự
          {
            type: "value",
            name: "Nhân sự",
            nameTextStyle: { color: "#94a3b8", fontSize: 11 },
            min: 0,
            axisLine: { show: false },
            splitLine: { lineStyle: { color: "#f1f5f9", type: "dashed" } },
            axisLabel: { color: "#94a3b8" }
          },
          // Trục phải: Tỷ lệ % đi làm
          {
            type: "value",
            name: "Tỷ lệ (%)",
            nameTextStyle: { color: "#94a3b8", fontSize: 11 },
            min: 0,
            max: 100,
            axisLine: { show: false },
            splitLine: { show: false },
            axisLabel: {
              color: "#10b981",
              formatter: "{value}%"
            }
          }
        ],
        series: [
          // Cột nền: Tổng nhân sự
          {
            name: "Tổng nhân sự",
            type: "bar",
            barWidth: 20,
            barGap: "-100%",
            data: points.map(point => point.totalEmployees),
            itemStyle: {
              color: "#e2e8f0",
              borderRadius: [6, 6, 0, 0]
            }
          },
          // Cột đi làm thực tế
          {
            name: "Đi làm",
            type: "bar",
            barWidth: 20,
            data: points.map(point => point.present),
            itemStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: "#6366f1" },
                { offset: 1, color: "#4338ca" }
              ]),
              borderRadius: [6, 6, 0, 0]
            }
          },
          // Đường tỷ lệ % đi làm
          {
            name: "Tỷ lệ đi làm",
            type: "line",
            yAxisIndex: 1,
            smooth: true,
            symbolSize: 8,
            data: points.map(point => Number(point.attendanceRate)),
            lineStyle: {
              width: 3,
              color: "#10b981"
            },
            itemStyle: {
              color: "#10b981",
              borderWidth: 2,
              borderColor: "#fff"
            }
          },
          {
            name: "Đi muộn",
            type: "bar",
            barWidth: 8,
            data: points.map(point => point.lateArrivals),
            itemStyle: { color: "#fbbf24", borderRadius: [4, 4, 0, 0] }
          }
        ]
      });
    }
  }
};
</script>

<style scoped>
.emp-attendance-stat-v3 {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  padding: 24px;
  box-shadow: 0 4px 20px -3px rgba(0, 0, 0, 0.04);
  margin-bottom: 28px;
}

/* Header */
.stat-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 14px;
  margin-bottom: 22px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f1f5f9;
}

.header-title-group {
  display: flex;
  align-items: center;
  gap: 14px;
}

.header-icon-badge {
  width: 44px;
  height: 44px;
  border-radius: 14px;
  background: #ede9fe;
  color: #7c3aed;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
}

.header-title {
  margin: 0 0 4px;
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
}

.header-subtitle {
  margin: 0;
  font-size: 13px;
  color: #64748b;
}

.live-indicator {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 700;
  color: #059669;
  background: #ecfdf5;
  padding: 5px 12px;
  border-radius: 999px;
  border: 1px solid #a7f3d0;
}

.pulse-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #10b981;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.25);
  animation: pulseDot 2s infinite;
}

@keyframes pulseDot {
  0% { transform: scale(0.95); opacity: 0.8; }
  50% { transform: scale(1.15); opacity: 1; }
  100% { transform: scale(0.95); opacity: 0.8; }
}

/* 6 KPI Cards Grid */
.emp-kpi-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 14px;
  margin-bottom: 24px;
}

.emp-kpi-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 14px 16px;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  transition: all 0.2s ease;
}

.emp-kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 14px rgba(0, 0, 0, 0.04);
}

.kpi-icon-wrap {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 19px;
  flex-shrink: 0;
}

.kpi-details {
  flex: 1;
  min-width: 0;
}

.kpi-name {
  display: block;
  font-size: 11.5px;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.kpi-number {
  font-size: 22px;
  font-weight: 800;
  line-height: 1.1;
  margin-bottom: 3px;
}

.kpi-unit {
  font-size: 11.5px;
  font-weight: 600;
  color: #94a3b8;
}

.kpi-hint {
  font-size: 10.5px;
  color: #94a3b8;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Border hints */
.border-indigo { border-left: 3.5px solid #4f46e5; }
.border-emerald { border-left: 3.5px solid #059669; }
.border-yellow { border-left: 3.5px solid #d97706; }
.border-rose { border-left: 3.5px solid #ef4444; }
.border-amber { border-left: 3.5px solid #f59e0b; }
.border-purple { border-left: 3.5px solid #8b5cf6; }

/* Colors */
.text-indigo { color: #4f46e5; }
.text-emerald { color: #059669; }
.text-yellow { color: #d97706; }
.text-rose { color: #ef4444; }
.text-amber { color: #f59e0b; }
.text-purple { color: #8b5cf6; }

.bg-indigo-soft { background: #e0e7ff; }
.bg-emerald-soft { background: #ecfdf5; }
.bg-yellow-soft { background: #fef3c7; }
.bg-rose-soft { background: #ffe4e6; }
.bg-amber-soft { background: #fef3c7; }
.bg-purple-soft { background: #f3e8ff; }

/* Charts Layout */
.charts-row {
  display: flex;
  flex-wrap: wrap;
  margin-bottom: -10px;
}

.charts-row::before,
.charts-row::after {
  display: none;
}

.charts-row > .el-col {
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
}

.chart-card {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;
  box-sizing: border-box;
}

.h-100 {
  height: 100%;
}

.chart-card-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}

.chart-title {
  margin: 0 0 3px;
  font-size: 15px;
  font-weight: 700;
  color: #1e293b;
}

.chart-subtitle {
  margin: 0;
  font-size: 12px;
  color: #94a3b8;
}

.tag-pill {
  font-size: 11px;
  font-weight: 700;
  padding: 3px 9px;
  border-radius: 8px;
}

.tag-pill--emerald { background: #dcfce7; color: #15803d; }
.tag-pill--blue { background: #e0f2fe; color: #0369a1; }

.chart-container {
  width: 100%;
  height: 240px;
  position: relative;
}

.chart-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  font-size: 13px;
}

.stat-api-error {
  margin: 12px 0;
}

.unmarked-note {
  margin: 8px 0 0;
  text-align: center;
  color: #64748b;
  font-size: 11px;
}

/* Gauge Footer */
.gauge-footer-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-top: auto;
  padding-top: 10px;
  border-top: 1px solid #e2e8f0;
}

.mini-stat-card {
  text-align: center;
  background: #ffffff;
  padding: 8px 4px;
  border-radius: 10px;
  border: 1px solid #f1f5f9;
}

.ms-label {
  display: block;
  font-size: 10.5px;
  color: #64748b;
  margin-bottom: 2px;
}

.ms-val {
  font-size: 12.5px;
  font-weight: 800;
}

/* Trend Legend Row */
.trend-legend-row {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  font-size: 11.5px;
  font-weight: 600;
  color: #475569;
  margin-top: auto;
  padding-top: 10px;
  border-top: 1px solid #e2e8f0;
  flex-wrap: wrap;
}

.legend-chip {
  display: flex;
  align-items: center;
  gap: 6px;
}

.chip-color {
  width: 12px;
  height: 12px;
  border-radius: 3px;
}

.chip-bar { background: #6366f1; }
.chip-bg { background: #cbd5e1; }
.chip-line {
  height: 3px;
  border-radius: 999px;
  background: #10b981;
}
.chip-late { background: #fbbf24; }

/* Responsive */
@media (max-width: 1300px) {
  .emp-kpi-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 768px) {
  .emp-kpi-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>
